import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface FilterButton {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface FilterButtonsProps {
  filterOptions: FilterButton[];
  appliedFilters: Record<string, any>;
  openFilter: (filterId: string, anchorEl: HTMLElement) => void;
  isDisabled: boolean;
}

const FilterButtons: React.FC<FilterButtonsProps> = ({
  filterOptions,
  appliedFilters,
  openFilter,
  isDisabled,
}) => {
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const iconMap: Record<string, React.ReactNode> = {
    level: <FilterListIcon className="w-4 h-4" />,
    size: <PeopleIcon className="w-4 h-4" />,
    session: <EventIcon className="w-4 h-4" />,
    classTime: <AccessTimeIcon className="w-4 h-4" />,
    format: <DesktopMacIcon className="w-4 h-4" />,
    status: <CheckCircleIcon className="w-4 h-4" />,
  };

  return (
    <motion.div
      className="flex flex-wrap gap-2 mb-6"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {filterOptions.map((filter) => (
        <motion.button
          key={filter.id}
          ref={(el) => {
            if (el) buttonRefs.current[filter.id] = el;
          }}
          data-filter-button
          onClick={(e) => {
            const button = e.currentTarget as HTMLElement;
            openFilter(filter.id, button);
          }}
          disabled={isDisabled}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
            appliedFilters[filter.id]
              ? 'bg-[#017e6e] text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {iconMap[filter.id] || filter.icon}
          {filter.label}
          {appliedFilters[filter.id] && (
            <span className="ml-1 text-xs font-bold">✓</span>
          )}
        </motion.button>
      ))}
    </motion.div>
  );
};

export default FilterButtons;
